const CANVAS_THEME_VARIABLES = Object.freeze({
  grid: "--color-canvas-grid",
  shapeFill: "--color-canvas-shape-fill",
  stroke: "--color-canvas-stroke",
  text: "--color-canvas-text",
  textMuted: "--color-text-muted",
});

export const DEFAULT_CANVAS_PALETTE = Object.freeze({
  grid: "rgba(237, 243, 251, 0.1)",
  shapeFill: "#111111",
  stroke: "#8bbcff",
  text: "#f8fafc",
  textMuted: "#b4c0cf",
});

// Colors the app itself assigned as defaults. Elements persisted with either
// theme's default values follow the active palette at render time; any other
// stored color is the user's explicit choice and renders verbatim.
const THEME_ADAPTIVE_FILLS = Object.freeze(["#111111", "#111827"]);
const THEME_ADAPTIVE_STROKES = Object.freeze(["#ff9f43"]);
const THEME_ADAPTIVE_TEXT_COLORS = Object.freeze(["#1f2937", "#f8fafc"]);
const THEME_ADAPTIVE_MUTED_TEXT_COLORS = Object.freeze([
  "#667085",
  "#6b7280",
  "#b4c0cf",
]);
const THEME_ADAPTIVE_LABEL_COLORS = Object.freeze([
  ...THEME_ADAPTIVE_TEXT_COLORS,
  ...THEME_ADAPTIVE_MUTED_TEXT_COLORS,
]);
const DARK_CONTRAST_TEXT = "#1f2937";
const LIGHT_CONTRAST_TEXT = "#f8fafc";
const TRANSPARENT_COLOR = "transparent";
const HEX_COLOR_PATTERN = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB_CHANNEL_MAX = 255;
const SRGB_LINEAR_THRESHOLD = 0.04045;
const SRGB_LINEAR_DIVISOR = 12.92;
const SRGB_OFFSET = 0.055;
const SRGB_SCALE = 1.055;
const SRGB_EXPONENT = 2.4;
const CONTRAST_LUMINANCE_OFFSET = 0.05;
const LUMINANCE_WEIGHTS = Object.freeze([0.2126, 0.7152, 0.0722]);

const readThemeVariable = ({ styles, fallback, variableName }) => {
  const value = styles.getPropertyValue(variableName).trim();
  return value || fallback;
};

/**
 * Resolves the canvas palette from the active theme's CSS variables. Falls
 * back to the dark palette outside a themed document (e.g. unit tests).
 * @returns {{ grid: string, shapeFill: string, stroke: string, text: string, textMuted: string }}
 */
export const readCanvasThemePalette = () => {
  if (typeof window === "undefined") return DEFAULT_CANVAS_PALETTE;
  const styles = window.getComputedStyle(document.documentElement);

  return {
    grid: readThemeVariable({
      fallback: DEFAULT_CANVAS_PALETTE.grid,
      styles,
      variableName: CANVAS_THEME_VARIABLES.grid,
    }),
    shapeFill: readThemeVariable({
      fallback: DEFAULT_CANVAS_PALETTE.shapeFill,
      styles,
      variableName: CANVAS_THEME_VARIABLES.shapeFill,
    }),
    stroke: readThemeVariable({
      fallback: DEFAULT_CANVAS_PALETTE.stroke,
      styles,
      variableName: CANVAS_THEME_VARIABLES.stroke,
    }),
    text: readThemeVariable({
      fallback: DEFAULT_CANVAS_PALETTE.text,
      styles,
      variableName: CANVAS_THEME_VARIABLES.text,
    }),
    textMuted: readThemeVariable({
      fallback: DEFAULT_CANVAS_PALETTE.textMuted,
      styles,
      variableName: CANVAS_THEME_VARIABLES.textMuted,
    }),
  };
};

const isThemeAdaptiveColor = ({ adaptiveColors, color }) =>
  typeof color === "string" && adaptiveColors.includes(color.toLowerCase());

const parseHexColor = (color) => {
  const match = typeof color === "string" ? color.match(HEX_COLOR_PATTERN) : null;
  if (!match) return null;
  return match.slice(1).map((channel) => Number.parseInt(channel, 16));
};

const convertSrgbChannel = (channel) => {
  const normalizedChannel = channel / RGB_CHANNEL_MAX;
  if (normalizedChannel <= SRGB_LINEAR_THRESHOLD) {
    return normalizedChannel / SRGB_LINEAR_DIVISOR;
  }
  return ((normalizedChannel + SRGB_OFFSET) / SRGB_SCALE) ** SRGB_EXPONENT;
};

const getRelativeLuminance = (color) => {
  const channels = parseHexColor(color);
  if (!channels) return null;
  return channels.reduce(
    (luminance, channel, index) =>
      luminance + convertSrgbChannel(channel) * LUMINANCE_WEIGHTS[index],
    0
  );
};

const getContrastRatio = (firstLuminance, secondLuminance) => {
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (
    (lighter + CONTRAST_LUMINANCE_OFFSET) /
    (darker + CONTRAST_LUMINANCE_OFFSET)
  );
};

const getContrastingTextColor = (fill) => {
  const fillLuminance = getRelativeLuminance(fill);
  if (fillLuminance === null) return null;
  const darkContrast = getContrastRatio(
    fillLuminance,
    getRelativeLuminance(DARK_CONTRAST_TEXT)
  );
  const lightContrast = getContrastRatio(
    fillLuminance,
    getRelativeLuminance(LIGHT_CONTRAST_TEXT)
  );
  return darkContrast >= lightContrast
    ? DARK_CONTRAST_TEXT
    : LIGHT_CONTRAST_TEXT;
};

/**
 * @param {{ color: string, palette: { shapeFill: string } }} params
 * @returns {string}
 */
export const resolveShapeFillColor = ({ color, palette }) =>
  isThemeAdaptiveColor({ adaptiveColors: THEME_ADAPTIVE_FILLS, color })
    ? palette.shapeFill
    : color;

/**
 * @param {{ color: string, palette: { stroke: string } }} params
 * @returns {string}
 */
export const resolveStrokeColor = ({ color, palette }) =>
  isThemeAdaptiveColor({ adaptiveColors: THEME_ADAPTIVE_STROKES, color })
    ? palette.stroke
    : color;

/**
 * @param {{ color: string, palette: { text: string } }} params
 * @returns {string}
 */
export const resolveTextColor = ({ color, palette }) => {
  if (isThemeAdaptiveColor({ adaptiveColors: THEME_ADAPTIVE_TEXT_COLORS, color })) {
    return palette.text;
  }
  if (
    isThemeAdaptiveColor({
      adaptiveColors: THEME_ADAPTIVE_MUTED_TEXT_COLORS,
      color,
    })
  ) {
    return palette.textMuted ?? palette.text;
  }
  return color;
};

/**
 * Resolves theme-authored shape labels against the shape fill, rather than
 * the canvas background. Explicit user-selected label colors stay unchanged.
 * @param {{ color: string, fill: string, palette: { text: string } }} params
 * @returns {string}
 */
export const resolveShapeLabelColor = ({ color, fill, palette }) => {
  const isAdaptive = isThemeAdaptiveColor({
    adaptiveColors: THEME_ADAPTIVE_LABEL_COLORS,
    color,
  });
  if (!isAdaptive) return color;
  if (fill === TRANSPARENT_COLOR) return palette.text;
  return getContrastingTextColor(fill) ?? palette.text;
};
